import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { Platform } from "@koc/shared";
import { apiKocs, apiVideos } from "../api.js";
import { Badge, Button, Card, PageHeader, Table, fmtNum, fmtTime } from "../components/ui.js";
import { KocEditDialog } from "./KocsPage.js";

const PLATFORM_TONE: Record<Platform, "blue" | "yellow" | "red"> = {
  tiktok: "blue",
  instagram: "yellow",
  youtube: "red",
};

export function KocDetailPage() {
  const { kocId } = useParams({ from: "/_layout/kocs/$kocId" });
  const id = Number(kocId);
  const qc = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);

  const koc = useQuery({ queryKey: ["koc", id], queryFn: () => apiKocs.get(id) });
  const videos = useQuery({
    queryKey: ["videos", { kocId: id }],
    queryFn: () => apiVideos.list({ kocId: id, limit: 50 }),
  });

  const remove = useMutation({
    mutationFn: () => apiKocs.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kocs"] });
      window.location.href = "/kocs";
    },
  });

  if (!koc.data) return <div className="text-stone-500">Loading…</div>;
  const k = koc.data;

  return (
    <>
      <PageHeader
        title={k.name}
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowEdit(true)}>Edit</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (confirm("Delete this KOC? Videos and pages keep their history (koc_id is set to NULL).")) {
                  remove.mutate();
                }
              }}
            >
              Delete KOC
            </Button>
          </>
        }
      />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="text-xs text-stone-500 mb-1">KOC notes</div>
          <div className="text-sm whitespace-pre-wrap">
            {k.notes || <span className="text-stone-400">none</span>}
          </div>
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">Created</div>
          <div className="text-sm">{fmtTime(k.created_at)}</div>
        </Card>
        <Card>
          <div className="text-xs text-stone-500 mb-1">Last update</div>
          <div className="text-sm">{fmtTime(k.updated_at)}</div>
        </Card>
      </div>

      <h2 className="text-lg font-semibold mb-2">Pages where this KOC publishes</h2>
      <Card className="mb-6">
        {k.pages.length === 0 ? (
          <span className="text-sm text-stone-400">Not linked to any project pages yet.</span>
        ) : (
          <ul className="divide-y divide-stone-100">
            {k.pages.map((p) => (
              <li key={p.page_id} className="flex items-center gap-3 py-2">
                <Badge tone={PLATFORM_TONE[p.platform]}>{p.platform}</Badge>
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: String(p.project_id) }}
                  className="text-sm font-medium text-stone-800 hover:underline w-40 truncate"
                >
                  {p.project_name}
                </Link>
                <span className="text-xs text-stone-500 w-32 truncate">{p.label || "(no label)"}</span>
                <a href={p.profile_url} target="_blank" rel="noreferrer" className="text-sm text-stone-700 hover:underline truncate flex-1">
                  {p.profile_url}
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <h2 className="text-lg font-semibold mb-2">Recent videos</h2>
      <Table
        rows={videos.data?.items ?? []}
        columns={[
          { header: "Platform", cell: (r) => <Badge tone={PLATFORM_TONE[r.platform]}>{r.platform}</Badge>, className: "w-24" },
          { header: "Project", cell: (r) => (
            <Link to="/projects/$projectId" params={{ projectId: String(r.project_id) }} className="text-xs hover:underline">
              {r.project_name}
            </Link>
          ), className: "w-40" },
          { header: "Title", cell: (r) => (
            <Link to="/videos/$videoId" params={{ videoId: String(r.id) }} className="hover:underline">
              {r.title || <span className="text-stone-400">(no title)</span>}
            </Link>
          )},
          { header: "Hit", cell: (r) => r.has_keyword_hit ? <Badge tone="green">hit</Badge> : null, className: "w-16" },
          { header: "Views", cell: (r) => fmtNum(r.latest_view_count), className: "text-right tabular-nums w-24" },
          { header: "Likes", cell: (r) => fmtNum(r.latest_like_count), className: "text-right tabular-nums w-24" },
          { header: "Published", cell: (r) => <span className="text-xs text-stone-500">{fmtTime(r.publish_time)}</span> },
        ]}
      />

      {showEdit && (
        <KocEditDialog
          onClose={() => setShowEdit(false)}
          initialId={id}
          initial={{ name: k.name, notes: k.notes }}
        />
      )}
    </>
  );
}
